import { useEffect, useState } from "react";
import clsx from "clsx";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { flattenMatchOdds } from "@/components/LichThiDauFull/slug";
import OddsStatus from "@/components/LichThiDauFull/OddsStatus";
import { CORNER_SRC } from "./icons";
import {
  kickoffParts,
  liveMinuteLabel,
  mergeLive,
  num,
  phaseOf,
  teamLabel,
} from "./model";

function Card({ kind, count }) {
  if (!(count > 0)) return null;
  const yellow = kind === "y";
  return (
    <span
      title={yellow ? "Thẻ Vàng" : "Thẻ Đỏ"}
      className={
        yellow
          ? "bg-yellow-400 text-black text-xs h-4 w-3 rounded flex justify-center items-center shrink-0"
          : "bg-red-600 text-white text-xs h-4 w-3 rounded flex justify-center items-center shrink-0"
      }
    >
      {count}
    </span>
  );
}

function Cards({ order, yellow, red }) {
  if (!(yellow > 0) && !(red > 0)) return null;
  const nodes = order.map((kind) => (
    <Card key={kind} kind={kind} count={kind === "y" ? yellow : red} />
  ));
  return <span className="flex gap-1 items-center shrink-0">{nodes}</span>;
}

function Logo({ src, placeholder }) {
  if (!src) {
    return placeholder ? <span className="w-5 h-5 shrink-0" /> : null;
  }
  return <img src={src} alt="" width={20} height={20} className="w-5 h-5 rounded-full shrink-0 object-cover" />;
}

function TimeCol({ match, phase }) {
  const row = "flex kbv-col items-center gap-1 lg:flex-row lg:items-center";
  if (phase === "upcoming") {
    const kick = kickoffParts(match.matchTime);
    return (
      <div className={row}>
        <span className="text-sm font-semibold whitespace-nowrap">{kick.time}</span>
        <span className="text-sm whitespace-nowrap">{kick.date}</span>
      </div>
    );
  }
  return (
    <div className={row}>
      <span className="text-sm font-semibold whitespace-nowrap">
        {phase === "live" ? liveMinuteLabel(match) : "KT"}
      </span>
      <span className="inline-flex items-center gap-0.5">
        <img src={CORNER_SRC} alt="" width={8} height={8} className="w-2 h-2" />
        <span className="text-sm">
          {num(match.homeCorner)}:{num(match.awayCorner)}
        </span>
      </span>
    </div>
  );
}

function Score({ score, half, win }) {
  return (
    <span className="kbv-score text-sm">
      <span className={clsx("kbv-score-ft", win && "font-bold")}>{score}</span>
      <span className="kbv-score-ht">
        <span>(</span>
        <span className="kbv-score-half">{half}</span>
        <span>)</span>
      </span>
    </span>
  );
}

function Side({
  name,
  logo,
  win,
  yellow,
  red,
  score,
  half,
  upcoming,
  side,
}) {
  const bold = win ? "font-bold" : "";
  const cards = side === "home" ? ["y", "r"] : ["r", "y"];
  return (
    <div className="flex items-center justify-between gap-1">
      <span className="flex gap-1 min-w-0 items-center">
        <Logo src={logo} />
        <span className={clsx("text-sm truncate", bold)}>{name}</span>
        <Cards order={cards} yellow={yellow} red={red} />
      </span>
      {upcoming ? null : <Score score={score} half={half} win={win} />}
    </div>
  );
}

function oddsPriceClass(trend) {
  const t = String(trend || "");
  if (t === "up" || t === "increased") return "text-green-600";
  if (t === "down" || t === "decreased") return "text-red-600";
  return "";
}

function handicapTexts(handicapValue, horizontal) {
  const n = Number(handicapValue);
  if (!Number.isFinite(n)) {
    return { homeText: "-", awayText: "-", isAway: false };
  }
  const abs = Math.abs(n);
  const isAway = n < 0;
  if (horizontal) {
    return {
      homeText: !isAway ? `-${abs}` : `+${abs}`,
      awayText: isAway ? `-${abs}` : `+${abs}`,
      isAway,
    };
  }
  return {
    homeText: !isAway ? `-${abs}` : null,
    awayText: isAway ? `-${abs}` : null,
    isAway,
  };
}

function OddsRow({ horizontal, handicapText, handicapClassName, trend, price }) {
  if (horizontal) {
    return (
      <div className="kbv-odds-row is-horizontal">
        <span className={clsx("text-sm text-right whitespace-nowrap", handicapClassName)}>
          {handicapText ?? ""}
        </span>
        <div className="w-3 flex items-center justify-center">
          <OddsStatus trend={trend} />
        </div>
        <span className={clsx("text-sm tabular-nums text-right whitespace-nowrap", oddsPriceClass(trend))}>
          {price}
        </span>
      </div>
    );
  }

  return (
    <div className="kbv-odds-row is-stacked">
      {handicapText ? (
        <span className={clsx("text-sm kbv-odds-hdp", handicapClassName)}>{handicapText}</span>
      ) : null}
      <div className="flex items-center gap-1">
        <OddsStatus trend={trend} />
        <span className={clsx("text-sm", oddsPriceClass(trend))}>{price}</span>
      </div>
    </div>
  );
}

function OddsBlock({
  horizontal,
  isAway,
  homeText,
  awayText,
  homeOdds,
  awayOdds,
  homeTrend,
  awayTrend,
}) {
  return (
    <div className={clsx("kbv-odds", horizontal ? "is-horizontal" : "is-stacked")}>
      <OddsRow
        horizontal={horizontal}
        handicapText={homeText}
        handicapClassName={horizontal ? (!isAway ? "text-red-600" : "text-green-600") : undefined}
        trend={homeTrend}
        price={homeOdds}
      />
      <OddsRow
        horizontal={horizontal}
        handicapText={awayText}
        handicapClassName={horizontal ? (isAway ? "text-red-600" : "text-green-600") : undefined}
        trend={awayTrend}
        price={awayOdds}
      />
    </div>
  );
}

export default function MatchRow({ match: rawMatch, isLast }) {
  const match = flattenMatchOdds(rawMatch);
  const { getScore, getOdds } = useLiveDataForMatch(match?.id);
  const liveScore = getScore();
  const liveOdds = getOdds();
  const merged = mergeLive(match, liveScore);
  const phase = phaseOf(merged.status);
  const filterOdds = useFilterOdds(match, liveOdds, null, -1);
  const odds = useMatchOdds(match, filterOdds, -1);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (phase !== "live") return undefined;
    const id = window.setInterval(() => setTick((value) => value + 1), 15000);
    return () => window.clearInterval(id);
  }, [phase]);

  const homeName = teamLabel(merged.homeTeam, merged.homeName);
  const awayName = teamLabel(merged.awayTeam, merged.awayName);
  const homeScore = num(merged.homeScore);
  const awayScore = num(merged.awayScore);
  const homeWin = homeScore > awayScore;
  const awayWin = awayScore > homeScore;
  const upcoming = phase === "upcoming";
  const finished = phase === "done";
  const showCards = !upcoming;
  const showOdds = !finished;

  const stackedHdp = handicapTexts(odds.handicapValue, false);
  const horizontalHdp = handicapTexts(odds.handicapValue, true);

  return (
    <div
      className={clsx(
        "kbv-match py-2.5 pl-2 pr-2 md:p-2",
        !isLast && "kbv-match-sep"
      )}
    >
      <div className="flex items-center gap-2 md:gap-1.5 lg:hidden">
        <div className="flex items-center gap-1 min-w-[60px]">
          <TimeCol match={merged} phase={phase} />
        </div>
        <div className="relative min-w-0 flex-1">
          <div className="flex kbv-col gap-1 md:gap-2">
            <Side
              side="home"
              name={homeName}
              logo={merged.homeTeam?.logo}
              win={homeWin}
              yellow={showCards ? num(merged.homeYellow) : 0}
              red={showCards ? num(merged.homeRed) : 0}
              score={homeScore}
              half={num(merged.homeHalfScore)}
              upcoming={upcoming}
            />
            <Side
              side="away"
              name={awayName}
              logo={merged.awayTeam?.logo}
              win={awayWin}
              yellow={showCards ? num(merged.awayYellow) : 0}
              red={showCards ? num(merged.awayRed) : 0}
              score={awayScore}
              half={num(merged.awayHalfScore)}
              upcoming={upcoming}
            />
          </div>
        </div>
        {showOdds ? (
          <OddsBlock
            horizontal={false}
            isAway={stackedHdp.isAway}
            homeText={stackedHdp.homeText}
            awayText={stackedHdp.awayText}
            homeOdds={odds.homeHandicapOdds}
            awayOdds={odds.awayHandicapOdds}
            homeTrend={odds.homeHandicapTrend}
            awayTrend={odds.awayHandicapTrend}
          />
        ) : null}
      </div>

      <div className="kbv-lg-grid lg:grid-cols-[170px_minmax(0,1fr)_196px] lg:items-center lg:gap-5">
        <div className="w-full flex items-center gap-1">
          <TimeCol match={merged} phase={phase} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="min-w-0 overflow-hidden flex items-center justify-center gap-1.5">
            <div className="w-[44%] min-w-0 flex items-center justify-end gap-1">
              {showCards ? <Cards order={["r", "y"]} yellow={num(merged.homeYellow)} red={num(merged.homeRed)} /> : null}
              <span className={clsx("text-sm truncate min-w-0", homeWin && "font-bold")}>{homeName}</span>
              <Logo src={merged.homeTeam?.logo} placeholder />
            </div>
            <div className="w-[56px] shrink-0 text-sm text-gray-700 text-center">
              {upcoming ? (
                <span className="font-semibold">VS</span>
              ) : (
                <span className="font-bold text-red-600 tabular-nums">
                  {homeScore} - {awayScore}
                </span>
              )}
            </div>
            <div className="w-[44%] min-w-0 flex items-center gap-1">
              <Logo src={merged.awayTeam?.logo} placeholder />
              <span className={clsx("min-w-0 max-w-[120px] truncate text-[13px]", awayWin && "font-bold")}>
                {awayName}
              </span>
              {showCards ? <Cards order={["y", "r"]} yellow={num(merged.awayYellow)} red={num(merged.awayRed)} /> : null}
            </div>
          </div>
        </div>
        {showOdds ? (
          <OddsBlock
            horizontal
            isAway={horizontalHdp.isAway}
            homeText={horizontalHdp.homeText}
            awayText={horizontalHdp.awayText}
            homeOdds={odds.homeHandicapOdds}
            awayOdds={odds.awayHandicapOdds}
            homeTrend={odds.homeHandicapTrend}
            awayTrend={odds.awayHandicapTrend}
          />
        ) : (
          <div />
        )}
      </div>
    </div>
  );
}
